// Libraries
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

// Components
import { ButtonV2 } from "#src/commonV2/Button";

// API-related
import type { IWorkspaceItem } from "#src/services/workspace";

// Children
import ConfirmDeleteWorkspaceModal from "./Confirm";
import { ModalHeaderIcon } from "#src/commonV2/modal/ModalHeaderIcon";
import { Trash01 } from "@untitled-ui/icons-react";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";

interface IDeleteWorkspaceModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  currentWorkspace: IWorkspaceItem;
}

const DeleteWorkspaceModal = ({
  workspaceId,
  open,
  onClose,
  currentWorkspace,
}: IDeleteWorkspaceModalProps) => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "detail.deleteWorkspace.modal.delete",
  });

  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);

  const onProcessToNextStep = () => {
    setOpenConfirmModal(true);
    onClose();
  };

  const displayWorkspaceName = currentWorkspace.short_name
    ? currentWorkspace.short_name
    : currentWorkspace.name;

  return (
    <>
      <ModalV2 open={open} onChangeOpen={onClose} className="w-[34rem] gap-8">
        <div className="flex gap-4">
          <ModalHeaderIcon type="error" Icon={Trash01} />
          <div className="flex flex-col gap-5">
            <span className="text-lg font-semibold text-gray-v2-900">
              {t("title")}
            </span>
            <span className="text-md text-gray-v2-600 whitespace-pre-line">
              <Trans
                t={t}
                i18nKey={"areYouSureToDeleteWorkspace"}
                values={{ name: displayWorkspaceName }}
                components={{ b: <b className="contents text-gray-v2-700" /> }}
              />
            </span>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <ButtonV2 variant="secondary" color="gray" onClick={onClose}>
            {t("button.cancel")}
          </ButtonV2>
          <ButtonV2 onClick={onProcessToNextStep}>
            {t("button.process")}
          </ButtonV2>
        </div>
      </ModalV2>
      <ConfirmDeleteWorkspaceModal
        workspaceId={workspaceId}
        open={openConfirmModal}
        onClose={() => {
          setOpenConfirmModal(false);
        }}
        currentWorkspace={currentWorkspace}
      />
    </>
  );
};

export default DeleteWorkspaceModal;
